import createDomPurify from "dompurify";
import formatNumber from "format-number";
import * as libphonenumber from "libphonenumber-js";

import * as strings from "../../strings";

import { TextElement } from "./LocaleUtils";

const DomPurify = createDomPurify();

export const validatePhoneNumber = (
	string: string,
	country?: string,
): Validity => {
	if (!string) {
		return createValidity(false, strings.FORM_ERROR_PHONE_NUMBER);
	}

	const result = parsePhoneNumber(string, country);

	if (result === undefined) {
		return createValidity(
			false,
			strings.FORM_ERROR_PHONE_NUMBER_MISSING_COUNTRY_CODE,
		);
	}

	if (!result.isValid()) {
		return createValidity(false, strings.FORM_ERROR_PHONE_NUMBER);
	}

	return createValidity(true);
};

export const parsePhoneNumber = (string: string, country?: string) => {
	let parsedCountry = country;
	if (
		typeof parsedCountry === "string" &&
		parsedCountry.toLowerCase() === "uk"
	) {
		parsedCountry = "GB";
	}
	return libphonenumber.parsePhoneNumberFromString(
		string || "",
		parsedCountry as any,
	);
};

export type Validity = {
	success: boolean;
	reason?: TextElement;
};

export const createValidity = (
	success: boolean,
	reason?: TextElement,
): Validity => {
	return {
		success,
		reason,
	};
};

export const validatePassword = (password: any): Validity => {
	// catch undefined's if no value has been added
	if (typeof password === "string") {
		if (password.length < 8) {
			return createValidity(false, strings.FORM_ERROR_PASSWORD_SHORT);
		}

		if (password.length > 256) {
			return createValidity(false, strings.FORM_ERROR_PASSWORD_LONG);
		}

		const passwordChars = password.split("");

		if (!passwordChars.some((char) => isNumber(char))) {
			return createValidity(false, strings.FORM_ERROR_PASSWORD_NUMBERLESS);
		}

		if (
			!passwordChars.some(
				(char) => isLetter(char) && char === char.toLowerCase(),
			)
		) {
			return createValidity(false, strings.FORM_ERROR_PASSWORD_NO_LOWERCASE);
		}

		if (
			!passwordChars.some(
				(char) => isLetter(char) && char === char.toUpperCase(),
			)
		) {
			return createValidity(false, strings.FORM_ERROR_PASSWORD_NO_UPPERCASE);
		}

		return createValidity(true);
	}
	return createValidity(false, strings.FORM_ERROR_PASSWORD_INVALID);
};

export const shortify = (string: string): string => {
	if (!string) return string;
	return toTitleCase(string)
		.split(" ")
		.map((string: string) => string.substring(0, 1))
		.join("");
};

export const toSnakeCase = (string: string): string => {
	return string.replace(/([A-Z])/g, ($1) => "_" + $1.toLowerCase());
};

export const toKebabCase = (string: string): string => {
	return String(string)
		.replace(/^[^A-Za-z0-9]*|[^A-Za-z0-9]*$/g, "")
		.replace(/([a-z])([A-Z])/g, (m, a, b) => a + "-" + b.toLowerCase())
		.replace(/[^A-Za-z0-9]+|_+/g, "-")
		.toLowerCase();
};

export const toCamelCase = (string: string): string => {
	const computedString = string
		.toLowerCase()
		.replace(/(?:(^.)|([-_\s]+.))/g, (match) =>
			match.charAt(match.length - 1).toUpperCase(),
		);
	return computedString.charAt(0).toLowerCase() + computedString.substring(1);
};

export const humanizeNumber = (
	number: number | string,
	decimalPlaces?: number,
): string => {
	return formatNumber({
		round: decimalPlaces,
	})(typeof number === "string" ? parseFloat(number) : number);
};

/**
 * Utility function for checking emails are valid.
 * Used in the forms components reducers/ middleware
 */

const EMAIL_REGEX =
	/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const isValidEmail = (email: string): boolean => {
	if (!email || typeof !email === "string") {
		return false;
	}
	const trimmedEmail = email.trim();
	return trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
};

const hashCodeCache: Record<string, number> = {};

export const hashCode = (string: string): number => {
	let hash = hashCodeCache[string] || 0;
	if (hash !== 0) return hash;
	if (typeof string !== "string") return string;
	if (string.length === 0) return hash;
	for (let i = 0; i < string.length; i++) {
		const chr = string.charCodeAt(i);
		hash = (hash << 5) - hash + chr;
		hash |= 0; // Convert to 32bit integer
	}
	return (hashCodeCache[string] = hash);
};

const titleCaseCache: Record<string, string> = {};

export const toTitleCase = <T extends string | null | undefined>(
	string: T,
	allCapsShort = false,
): T => {
	if (typeof string !== "string") return string;

	let result =
		titleCaseCache[string] ||
		string
			.replace(/([A-Z]+)+([A-Z][a-z])/g, "$1 $2")
			.replace(new RegExp("(_|-)", "g"), " ")
			.replace(
				/[A-Za-zÀ-ÖØ-öø-ÿĀ-ƿǄ-ǿ]\S*/g,
				(txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
			)
			.trim();

	titleCaseCache[string] = result;

	if (allCapsShort) {
		result = result
			.split(" ")
			.map((string) => (string.length <= 3 ? string.toUpperCase() : string))
			.join(" ");
	}

	return result as T;
};

export const stringPrettify = (string: string): string => {
	return toTitleCase(
		string // insert a space before all caps
			.replace(/([A-Z])/g, " $1"),
	);
};

export const uuid = (length = 24): string => {
	function s4() {
		return Math.floor((1 + Math.random()) * 0x10000)
			.toString(16)
			.substring(1);
	}

	let string = "";
	while (string.length < length) {
		string += s4();
	}

	return string.substring(0, length);
};

export const isLetter = (string: string): boolean => {
	return string.length === 1 && /[a-z]/i.test(string);
};

export const isNumber = (string: string): boolean => {
	return string.length === 1 && /[0-9]/i.test(string);
};

export const repeat = (string: string, times: number): string =>
	new Array(times + 1).join(string);

export const padNumber = (
	stringOrNumber: string | number,
	maxLength: number,
): string => {
	const string = stringOrNumber.toString();
	return string.length >= maxLength
		? string
		: repeat("0", maxLength - string.length) + string;
};

export const cleanHTMLString = (
	string: string,
	options: createDomPurify.Config = {},
) => {
	return String(DomPurify.sanitize(string, options));
};
